import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, ShieldCheck, KeyRound, Smartphone, Copy } from "lucide-react";
import { toast } from "sonner";

/** MFA challenge UI rendered after the password step. Handles BOTH setup
 *  (first-time enrollment) and verify (subsequent logins). The parent
 *  passes `challenge = { action: 'setup' | 'verify', temp_token }` and
 *  `onComplete(data)` which the parent uses to persist the issued tokens.
 *  Verify supports switching to a recovery code instead of TOTP.
 *  Setup surfaces the auto-generated recovery codes ONCE.
 */
export default function MfaPanel({ challenge, onComplete, onCancel }) {
  const { action, temp_token } = challenge;
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("totp"); // verify-only: "totp" | "recovery"
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [postSetupCodes, setPostSetupCodes] = useState(null);
  const [postSetupSession, setPostSetupSession] = useState(null);
  const [codesAck, setCodesAck] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (action !== "setup") return;
    let cancelled = false;
    api.post("/auth/mfa/setup/begin", { temp_token })
      .then(({ data }) => { if (!cancelled) setSetupData(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [action, temp_token]);

  useEffect(() => {
    if (postSetupCodes) return; // don't refocus during the codes-display step
    if (action === "verify") codeRef.current?.focus();
    if (action === "setup" && setupData) codeRef.current?.focus();
  }, [action, setupData, postSetupCodes]);

  const isRecovery = action === "verify" && mode === "recovery";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim();
    if (isRecovery) {
      if (trimmed.length < 6) {
        setError("Enter the recovery code from when you set up two-factor.");
        return;
      }
    } else if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code shown in your authenticator app.");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = action === "setup" ? "/auth/mfa/setup/verify" : "/auth/mfa/verify";
      const { data } = await api.post(endpoint, {
        temp_token,
        code: trimmed,
        remember_device: remember,
      });
      // Setup flow returns recovery_codes once — show them before completing.
      if (action === "setup" && Array.isArray(data?.recovery_codes) && data.recovery_codes.length) {
        setPostSetupCodes(data.recovery_codes);
        setPostSetupSession(data);
      } else {
        onComplete(data);
      }
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  // After-setup: show recovery codes once and require acknowledgement.
  if (postSetupCodes) {
    const copyAll = () => {
      navigator.clipboard.writeText(postSetupCodes.join("\n"));
      toast.success("Codes copied");
    };
    return (
      <div className="space-y-4" data-testid="mfa-setup-recovery-codes">
        <div className="flex items-center gap-2 text-[#166534]">
          <KeyRound className="w-5 h-5" strokeWidth={1.75} />
          <div className="font-display text-xl font-semibold">Save your recovery codes</div>
        </div>
        <p className="text-sm text-[#334155]">
          These one-time codes let you sign in if you lose access to your authenticator app.
          They will not be shown again — store them somewhere safe.
        </p>
        <ul className="grid grid-cols-2 gap-2" data-testid="mfa-setup-codes-list">
          {postSetupCodes.map((c, i) => (
            <li key={i} data-testid={`mfa-setup-code-${i}`}
              className="px-3 py-2 bg-[#F9F8F6] rounded-md font-mono text-sm text-[#0F172A] border border-[#E5E5E0]">
              {c}
            </li>
          ))}
        </ul>
        <button type="button" onClick={copyAll}
          data-testid="mfa-setup-codes-copy"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs font-medium text-[#334155] hover:bg-[#F9F8F6]">
          <Copy className="w-3 h-3" /> Copy all
        </button>
        <label className="flex items-start gap-2 text-sm text-[#334155]">
          <input type="checkbox" checked={codesAck} onChange={(e) => setCodesAck(e.target.checked)}
            data-testid="mfa-setup-codes-ack" className="mt-1" />
          I&apos;ve saved these codes somewhere safe.
        </label>
        <button type="button" disabled={!codesAck}
          onClick={() => onComplete(postSetupSession)}
          data-testid="mfa-setup-codes-continue"
          className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-50">
          Continue to HarborWell Connect
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid={action === "setup" ? "mfa-setup-form" : "mfa-verify-form"}
    >
      <div className="flex items-center gap-2 text-[#1E3A8A]">
        <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
        <div className="font-display text-xl font-semibold">
          {action === "setup" ? "Set up two-factor sign-in" : "Two-factor sign-in"}
        </div>
      </div>

      {action === "setup" && (
        <div className="space-y-4">
          <p className="text-sm text-[#334155]">
            HarborWell Connect requires a second sign-in step. Scan the QR code
            below with Microsoft Authenticator, Google Authenticator, Authy, or
            any TOTP app, then enter the 6-digit code it generates.
          </p>
          {!setupData ? (
            <div className="flex items-center gap-2 text-[#1E3A8A] text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating your secret…
            </div>
          ) : (
            <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-center">
              <img
                src={setupData.qr_png_data_url}
                alt="Scan to enroll in two-factor authentication"
                className="w-40 h-40 border border-[#E5E5E0] rounded-xl bg-white p-1"
                data-testid="mfa-qr-image"
              />
              <div className="text-xs text-[#334155] space-y-2 break-words">
                <div className="font-medium text-[#0F172A] inline-flex items-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5" /> Scan with your authenticator
                </div>
                <div className="text-[#7E937D]">
                  Can&apos;t scan? Add the account manually:
                </div>
                <div>
                  <div>Issuer: <span className="font-mono text-[#0F172A]">{setupData.issuer}</span></div>
                  <div>Account: <span className="font-mono text-[#0F172A]">{setupData.account}</span></div>
                  <div className="break-all">
                    Secret: <span className="font-mono text-[#0F172A]" data-testid="mfa-manual-secret">{setupData.manual_secret}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {action === "verify" && (
        <p className="text-sm text-[#334155]">
          {isRecovery
            ? "Enter one of your saved recovery codes to sign in. Each code works once."
            : "Enter the 6-digit code from your authenticator app to finish signing in."}
        </p>
      )}

      <div>
        <label className="block text-sm font-medium text-[#334155] mb-2 inline-flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5" />
          {isRecovery ? "Recovery code" : "6-digit code"}
        </label>
        <input
          ref={codeRef}
          type="text"
          inputMode={isRecovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          maxLength={isRecovery ? 24 : 6}
          value={code}
          onChange={(e) => {
            const v = e.target.value;
            setCode(isRecovery ? v.toUpperCase() : v.replace(/\D/g, "").slice(0, 6));
          }}
          className={`w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] focus:border-transparent text-[#0F172A] font-mono text-lg ${
            isRecovery ? "tracking-[0.1em]" : "tracking-[0.5em] text-center"
          }`}
          placeholder={isRecovery ? "XXXX-XXXX-XXXX" : "••••••"}
          data-testid="mfa-code-input"
          required
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-[#334155] select-none cursor-pointer">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          data-testid="mfa-remember-checkbox"
          className="w-4 h-4 rounded border-[#E5E5E0] text-[#1E3A8A] focus:ring-[#7E937D]"
        />
        Trust this device for 30 days
      </label>

      {error && (
        <div
          data-testid="mfa-error"
          className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || (action === "setup" && !setupData)}
        data-testid="mfa-submit-btn"
        className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting
          ? "Verifying…"
          : action === "setup"
          ? "Verify & finish enrollment"
          : "Verify and sign in"}
      </button>

      {action === "verify" && (
        <button
          type="button"
          onClick={() => { setMode(isRecovery ? "totp" : "recovery"); setCode(""); setError(""); }}
          data-testid="mfa-toggle-recovery"
          className="block w-full text-xs text-[#1E3A8A] hover:underline"
        >
          {isRecovery ? "Use authenticator app instead" : "Lost your device? Use a recovery code"}
        </button>
      )}

      <button
        type="button"
        onClick={onCancel}
        data-testid="mfa-cancel-btn"
        className="block w-full text-xs text-[#1E3A8A] hover:underline"
      >
        Back to sign in
      </button>
    </form>
  );
}
